/**
 * i18n TypeScript Types
 *
 * Type definitions for the internationalization system
 */

/**
 * Supported locales - BCP 47 format with hyphen
 */
export type Locale = 'zh-CN' | 'en-US';

/**
 * Default locale
 */
export const DEFAULT_LOCALE: Locale = 'zh-CN';

/**
 * Supported locales array for iteration
 */
export const SUPPORTED_LOCALES: readonly Locale[] = ['zh-CN', 'en-US'] as const;

/**
 * Locale configuration for display purposes
 */
export interface LocaleOption {
  value: Locale;
  label: string;
  flag: string;
}

/**
 * Available locale options for UI selectors
 */
export const LOCALE_OPTIONS: LocaleOption[] = [
  { value: 'zh-CN', label: '简体中文', flag: '🇨🇳' },
  { value: 'en-US', label: 'English', flag: '🇺🇸' },
];

/**
 * Translation namespace types
 * Each namespace corresponds to a module or common UI elements
 */
export type TranslationNamespace =
  | 'common'
  | 'nav'
  | 'validation'
  | 'errors'
  | 'masterData'
  | 'inventory'
  | 'sales'
  | 'procurement'
  | 'production'
  | 'finance'
  | 'reports'
  | 'documents'
  | 'admin';

/**
 * All available namespaces
 */
export const DEFAULT_NAMESPACES: TranslationNamespace[] = [
  'common',
  'nav',
  'validation',
  'errors',
];

/**
 * Module namespaces (for lazy loading)
 */
export const MODULE_NAMESPACES: TranslationNamespace[] = [
  'masterData',
  'inventory',
  'sales',
  'procurement',
  'production',
  'finance',
  'reports',
  'documents',
  'admin',
];

/**
 * i18n configuration options
 */
export interface I18nConfig {
  defaultLocale: Locale;
  fallbackLocale: Locale;
  supportedLocales: Locale[];
  defaultNamespace: TranslationNamespace;
  namespaces: TranslationNamespace[];
}

/**
 * Default i18n configuration
 */
export const defaultI18nConfig: I18nConfig = {
  defaultLocale: DEFAULT_LOCALE,
  fallbackLocale: 'zh-CN',
  supportedLocales: [...SUPPORTED_LOCALES],
  defaultNamespace: 'common',
  namespaces: [...DEFAULT_NAMESPACES, ...MODULE_NAMESPACES],
};

/**
 * Format options for date/time
 */
export interface DateFormatOptions {
  dateStyle?: 'full' | 'long' | 'medium' | 'short';
  timeStyle?: 'full' | 'long' | 'medium' | 'short';
}

/**
 * Format options for numbers
 */
export interface NumberFormatOptions {
  style?: 'decimal' | 'percent';
  minimumFractionDigits?: number;
  maximumFractionDigits?: number;
}

/**
 * Format options for currency
 */
export interface CurrencyFormatOptions {
  currency: string;
  minimumFractionDigits?: number;
  maximumFractionDigits?: number;
}

/**
 * Localized error response from API
 */
export interface LocalizedApiError {
  code: string;
  message: string;
  details?: Record<string, unknown>;
}
